// @flow

import React, { useState } from "react";

import { Stack, Box, TextField, IconButton, Button, Snackbar, Alert, CircularProgress, Typography, Paper } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import axios from 'axios';

import { TENANT } from "../../constants";

import { useAppContext } from "../../context/AppContext";

import { useLanguage } from "../../i18n";

const ConfirmEmailStep = (): any => {
    const { state, setState } = useAppContext();
    const { t } = useLanguage();

    const [inProgress, setInProgress] = React.useState(false);

    const [statusMessage, setStatusMessage] = React.useState(null);

    const [snackbarMessage, setSnackbarMessage] = React.useState(null);
    const handleShowError = (error: string) => {
        setSnackbarMessage(error);
    };
    const handleSnackbarClose = (_, reason) => {
        // Prevent closing on clickaway (optional)
        if (reason === "clickaway") return;
        setSnackbarMessage(null);
    };

    const [isEditing, setIsEditing] = useState(false);

    const handleEdit = () => setIsEditing(true);

    const setEmail = (email: any) => {
        let newState = state;
        if (email != null) {
            newState = {
                ...newState,
                returnRequest: {
                    ...newState.returnRequest,
                    customer: {
                        ...newState.returnRequest.customer,
                        email,
                    }
                }
            }
        }

        setState(newState);
    }

    const handleConfirm = async () => {
        setInProgress(true);

        try {
            const url = state.returnRequest._links.changeCustomerEmail.href;
            const response = await axios.post(
                url,
                {
                    email
                },
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    }
                },
            );
            
            setState({ ...state, returnRequest: response.data });

            // Generate return shipping
            const generateShippingUrl = response.data._links.generateReturnShipping.href;
            const generateShippingResponse = await axios.post(
                generateShippingUrl,
                {
                },
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    }
                },
            );

            // Update state with the response after shipping is generated
            setState({ ...state, returnRequest: generateShippingResponse.data });

            setStatusMessage(t('returnShippingSuccess'));
        } catch (error) {
            console.error("Error update picking address:", error);

            const errorData = error.response?.data;
            if (errorData?.causes && Array.isArray(errorData.causes)) {
                const messages = errorData.causes
                    .map((cause) => cause.message)
                    .join("; ");
                handleShowError(messages);
            } else {
                handleShowError(
                    errorData?.message || t('errorOccurred'),
                );
            }
        } finally {
            setInProgress(false);
        }
    }

    const email = state.returnRequest.customer.email;

    const isNew = state.returnRequest?.state === 'NEW';

    return (
        <>
            <Snackbar
                open={snackbarMessage != null}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Stack>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <TextField
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!isEditing}
                        sx={{
                            "& .MuiInputBase-root": {
                                bgcolor: isEditing ? "#fff" : "#f0f0f0",
                            },
                        }}
                    />
                    {!isEditing && (
                        <IconButton onClick={handleEdit} color="primary">
                            <EditIcon />
                        </IconButton>
                    )}
                </Box>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleConfirm}
                    
                    sx={{ mt: 1, mr: 1 }}

                    disabled={!isNew || inProgress}
                    startIcon={
                        inProgress ? <CircularProgress size={20} color="inherit" /> : null
                    }
                >
                    {t('confirmEmail')}
                </Button>
            </Stack>

            {statusMessage ? (
                <Paper
                    elevation={0}
                    sx={{
                        mt: 3,
                        p: 3,
                        bgcolor: 'success.light',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'success.main'
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'success.dark',
                            fontWeight: 500,
                            textAlign: 'center'
                        }}
                    >
                        {statusMessage}{' '}
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 700,
                                color: 'success.dark'
                            }}
                        >
                            {email}
                        </Typography>
                    </Typography>
                </Paper>
            ) : null}
        </>
    );
};

export default ConfirmEmailStep;
